import React from "react";
import {
  Typography,
  Divider,
  Button,
  Empty,
  Space,
  Table,
  Input,
  Switch,
  Tag,
} from "antd";
import { CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { openNotification } from "../../../../Components/Notification";
import { useLoader } from "../../../../Context/LoaderContext";
import { ApiKey } from "../../../../Entities/ApiKey";
import apiKeyService from "../../../../Service/api.key.service";
import dayjs from "dayjs";
import "./index.scss";

interface APIKeysProps {
  apiKeys: ApiKey[];
  setApiKeys: React.Dispatch<React.SetStateAction<ApiKey[]>>;
}

const APIKeys: React.FC<APIKeysProps> = ({ apiKeys, setApiKeys }) => {
  const { isLoading, setLoading, setLoadingMessage } = useLoader();

  const handleCopyToClipboard = async (key: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(key);
        openNotification("success", "Copied", "API Key copied to clipboard");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = key;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            openNotification(
              "success",
              "Copied",
              "API Key copied to clipboard"
            );
          } else {
            throw new Error("Copy command failed");
          }
        } catch (err) {
          throw new Error("Copy command failed");
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      openNotification(
        "error",
        "Failed to copy API Key",
        "Please copy the key manually from the input field"
      );
    }
  };

  const handleGenerateAPIKeyClick = async () => {
    try {
      setLoading(true);
      setLoadingMessage("Generating API key...");
      const generatedApiKey = await apiKeyService.generateAPIKey();
      if (generatedApiKey) {
        setApiKeys([...apiKeys, generatedApiKey]);
      }
    } catch (error: any) {
      console.error("Error generating API key:", error);
      openNotification("error", "Error", error.message);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleToggleStatus = async (record: any, checked: boolean) => {
    try {
      setLoading(true);
      setLoadingMessage("Updating API key status...");
      const newStatus = checked ? "active" : "inactive";
      await apiKeyService.updateAPIKey(record._id, newStatus);
      setApiKeys(
        apiKeys.map((key) =>
          key._id === record._id ? { ...key, status: newStatus } : key
        )
      );
      openNotification("success", "Success", "API key status updated");
    } catch (error: any) {
      console.error("Error updating API key status:", error);
      openNotification("error", "Error", error.message);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const tableData = apiKeys.map((key, index) => ({
    key: key._id,
    no: index + 1,
    createdAt: key.createdAt,
    apiKey: key.apiKey,
    status: key.status,
    _id: key._id,
  }));

  const columns = [
    {
      title: "No.",
      dataIndex: "no",
      key: "no",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string, record: any) => {
        const date = dayjs(record.createdAt);
        return (
          <div>
            <div>{date.format("DD-MMM-YYYY")}</div>
            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
              {date.format("HH:mm A")}
            </Typography.Text>
          </div>
        );
      },
    },
    {
      title: "API Key",
      dataIndex: "apiKey",
      key: "apiKey",
      render: (text: string, record: any) => (
        <Space.Compact style={{ width: "100%" }}>
          <Input defaultValue={text} readOnly id={`api-key-${record._id}`} />
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => handleCopyToClipboard(text)}
          />
        </Space.Compact>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text: string, record: any) => (
        <Tag color={record.status === "active" ? "green" : "red"}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text: string, record: any) => (
        <Switch
          checked={record.status === "active"}
          onChange={(checked) => handleToggleStatus(record, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
  ];

  return (
    <section className="integration-tab-content-section">
      <div className="integration-details-section-header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          API Keys
        </Typography.Title>

        <Divider style={{ margin: "0" }} />
      </div>
      <section className="integration-tab-content-section-content">
        <Typography.Paragraph>
          <strong>QualifyMe</strong> API keys are used to authenticate and
          enable integrations with QualifyMe services such as APIs, widgets, and
          web applications.
        </Typography.Paragraph>

        <div className="sub-section-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            My API Keys
          </Typography.Title>

          {apiKeys.length > 0 && (
            <Button
              type="primary"
              onClick={handleGenerateAPIKeyClick}
              loading={isLoading}
              icon={<PlusOutlined />}
            >
              New API Key
            </Button>
          )}
        </div>

        <div>
          <Table
            dataSource={tableData}
            columns={columns}
            locale={{
              emptyText: (
                <Empty
                  description="Looks like you haven't created an API key yet. Click below to generate one and get started."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  <Button
                    type="primary"
                    onClick={handleGenerateAPIKeyClick}
                    loading={isLoading}
                    style={{ marginTop: 16 }}
                  >
                    Generate API Key
                  </Button>
                </Empty>
              ),
            }}
          />
        </div>
      </section>
    </section>
  );
};

export default APIKeys;
